import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { FormMode } from '../../../../enums/form-mode';
import OptionModal from '../../../../components/modals/option-modal';
import Notes from './notes';
import css from '../index.module.css';

export interface ItemWithNotes {
  id: string;
  notes: string | null;
}

export function useNotes(name: string, mode: FormMode, idName: string = 'id') {
  const { t } = useTranslation();
  const [modal, setModal] = useState<React.ReactElement | null>(null);
  const form = useFormContext();
  const { setValue, getValues, register } = form;

  const editNotes = useCallback(
    (row: ItemWithNotes) => {
      const handleSave = () => {
        const notes = getValues(`${name}.${row[idName]}.temporary_notes`) || '';
        setValue(`${name}.${row[idName]}.notes`, notes);
        setModal(null);
      };

      const notes = getValues(`${name}.${row[idName]}.notes`) || '';
      setValue(`${name}.${row[idName]}.temporary_notes`, notes);

      const buttons = [];
      if (mode !== FormMode.view) {
        buttons.push(
          {
            title: t('pages.crop.vaad_liz_reports_v2.notes_modal.save'),
            name: 'save',
            value: 'save',
          },
          {
            title: t('pages.crop.vaad_liz_reports_v2.notes_modal.cancel'),
            name: 'cancel',
            value: 'cancel',
          }
        );
      }

      const title =
        mode === FormMode.view
          ? t('pages.crop.vaad_liz_reports_v2.notes_modal.title.view')
          : t('pages.crop.vaad_liz_reports_v2.notes_modal.title.edit');

      setModal(
        <OptionModal
          title={title}
          content={
            <textarea
              name={`${name}.${row[idName]}.temporary_notes`}
              rows={10}
              readOnly={mode === FormMode.view}
              disabled={mode === FormMode.view}
              className={css['textarea']}
              {...register(`${name}.${row[idName]}.temporary_notes`)}
            />
          }
          buttons={buttons}
          onChoice={(choice) => {
            if (choice === 'save') {
              handleSave();
            }
            setModal(null);
          }}
          onClose={() => setModal(null)}
        />
      );
    },
    [getValues, idName, mode, name, register, setValue, t]
  );

  const getNotesElement = useCallback(
    (name: string, row: ItemWithNotes) => {
      return <Notes name={name} row={row} editNotes={editNotes} mode={mode} />;
    },
    [editNotes, mode]
  );

  return { editNotes, getNotesElement, modal };
}
